import type { ReactElement } from "react";
import { css } from "@emotion/react";
import Close from "@mui/icons-material/Close";
import Done from "@mui/icons-material/Done";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { Box, Typography, useTheme } from "@mui/material";
import type { Id, ToastOptions } from "react-toastify";
import { toast, ToastContainer } from "react-toastify";
import type { Promisable } from "type-fest";

import "react-toastify/ReactToastify.css";

export const dismissToast = (toastId: Id) => {
  toast.dismiss(toastId);
};

interface Styles {
  root: ReturnType<typeof css>;
}

type IconType =
  | ReactElement<typeof InfoOutlined>
  | ReactElement<typeof Done>
  | ReactElement<typeof Close>;

export interface ToastAction {
  label: string;
  onClick: () => Promisable<void>;
}

interface ToastNotificationsProps {
  message: string;
  actionButton?: ToastAction;
  showCloseIcon?: boolean;
  autoClose?: ToastOptions["autoClose"];
}

export type RenderedToastNotificationsProps = Omit<
  ToastNotificationsProps,
  "autoClose"
> & { icon?: IconType };

export function ToastNotifications() {
  const theme = useTheme();

  const styles: Styles = {
    root: css`
      width: 100%;
      & .Toastify__toast-container {
        width: auto;
        max-width: 450px;
        & .Toastify__toast {
          padding: 0;
          border-radius: 0;
          margin-bottom: ${theme.spacing(2.5)};
          min-height: 0;
        }
        & .close-toast-container {
          margin: ${theme.spacing(2)};
        }
        & .close-toast {
          position: absolute;
          right: ${theme.spacing(2)};
          transform: translate(-45%, -45%);
        }
      }
    `,
  };

  return (
    <Box css={styles.root}>
      <ToastContainer
        position="top-right"
        hideProgressBar={true}
        closeOnClick={false}
        draggable={false}
        pauseOnHover={true}
        style={{ backgroundColor: "transparent" }}
      />
    </Box>
  );
}

function ToastButton({ label, onClick }: ToastAction) {
  const theme = useTheme();
  return (
    <Box
      onClick={() => {
        void onClick();
      }}
      sx={{
        display: "flex",
        alignItems: "center",
      }}
    >
      <Typography
        sx={{
          fontWeight: 600,
          color: "primary.main",
          fontSize: theme.spacing(1.625),
        }}
        css={css`
          &:hover {
            cursor: pointer;
          }
        `}
      >
        {label}
      </Typography>
    </Box>
  );
}

function StylesToast() {
  const theme = useTheme();
  return {
    toast: css`
      display: flex;
      align-items: center;
      padding: ${theme.spacing(2, 3, 2, 3)};
      gap: ${theme.spacing(2)};
    `,
    toastText: css`
      font-weight: 400;
      font-size: ${theme.spacing(1.625)};
      color: ${theme.palette.text.primary};
    `,
  };
}

export function Toast({
  message,
  actionButton,
  showCloseIcon,
  icon,
}: RenderedToastNotificationsProps) {
  return (
    <Box css={StylesToast().toast}>
      {icon}
      <Typography css={StylesToast().toastText}>{message}</Typography>
      {actionButton && <ToastButton {...actionButton} />}
      {showCloseIcon && (
        <Box className="close-toast-container">
          <Box className="close-toast">
            <Close
              sx={{ color: "primary.hint" }}
              fontSize="small"
              css={css`
                &:hover {
                  cursor: pointer;
                }
              `}
              onClick={() => {
                toast.dismiss();
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

const DEFAULT_ON_CLOSE_TIMEOUT_MS = 30000;
const DEFAULT_POSITION = "top-right";
export const renderSuccessToast = ({
  message,
  actionButton,
  showCloseIcon = true,
  autoClose = DEFAULT_ON_CLOSE_TIMEOUT_MS,
}: ToastNotificationsProps) => {
  toast.success(
    <Toast
      message={message}
      actionButton={actionButton}
      showCloseIcon={showCloseIcon}
      icon={<Done sx={{ color: "secondary.main" }} />}
    />,
    {
      autoClose: autoClose,
      closeButton: false,
      icon: false,
      position: DEFAULT_POSITION,
      style: {
        backgroundColor: "#F2FCF9",
        border: "2px solid #00BF7C",
        borderRadius: "6px",
      },
    },
  );
};

export const renderInfoToast = ({
  message,
  actionButton,
  showCloseIcon = true,
  autoClose = DEFAULT_ON_CLOSE_TIMEOUT_MS,
}: ToastNotificationsProps): Id => {
  return toast.info(
    <Toast
      message={message}
      actionButton={actionButton}
      showCloseIcon={showCloseIcon}
      icon={<InfoOutlined sx={{ color: "primary.main" }} />}
    />,
    {
      autoClose: autoClose,
      closeButton: false,
      icon: false,
      position: DEFAULT_POSITION,
      style: {
        backgroundColor: "#F6F9FD",
        border: "2px solid #4171BF",
        borderRadius: "6px",
      },
    },
  );
};

export const renderWarningToast = ({
  message,
  actionButton,
  showCloseIcon = true,
  autoClose = DEFAULT_ON_CLOSE_TIMEOUT_MS,
}: ToastNotificationsProps) => {
  toast.error(
    <Toast
      message={message}
      actionButton={actionButton}
      showCloseIcon={showCloseIcon}
      icon={<InfoOutlined sx={{ color: "warning.main" }} />}
    />,
    {
      autoClose: autoClose,
      closeButton: false,
      icon: false,
      position: DEFAULT_POSITION,
      style: {
        backgroundColor: "#FFFCF3",
        border: "2px solid #FDC910",
        borderRadius: "6px",
      },
    },
  );
};

export const renderErrorToast = ({
  message,
  actionButton,
  showCloseIcon = true,
  autoClose = DEFAULT_ON_CLOSE_TIMEOUT_MS,
}: ToastNotificationsProps) => {
  toast.error(
    <Toast
      message={message}
      actionButton={actionButton}
      showCloseIcon={showCloseIcon}
      icon={<InfoOutlined sx={{ color: "error.main" }} />}
    />,
    {
      autoClose: autoClose,
      closeButton: false,
      icon: false,
      position: DEFAULT_POSITION,
      style: {
        backgroundColor: "#FEF6F4",
        border: "2px solid #EC501C",
        borderRadius: "6px",
      },
    },
  );
};
